if (!customElements.get('subscription-module')) {
  customElements.define(
    'subscription-module',
    class SubscriptionModule extends HTMLElement {
      constructor() {
        super();
        // Initialize core state properties
        this.variantId = this.dataset.variantId;
        this.isSubscriptionOnly = Boolean(this.dataset.subscriptionOnly);
        this.skusToAdd = this.getMetafieldSkusToAdd();

        // Bind methods to preserve context
        this.handlePurchaseOptionChange = this.handlePurchaseOptionChange.bind(this);
        this.handleFrequencyChange = this.handleFrequencyChange.bind(this);
        this.resetState = this.resetState.bind(this);
        this.validateSubscriptionQuantity = this.validateSubscriptionQuantity.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.getFrequencyText = this.getFrequencyText.bind(this);

        this.translations = document.querySelector('[data-subscription-translations]')
          ? JSON.parse(document.querySelector('[data-subscription-translations]').textContent)
          : {};
      }

      isSubscriptionSelected() {
        const selectedOption = this.querySelector('input[type="radio"]:checked');
        return selectedOption?.dataset.radioType === 'selling_plan';
      }

      getCurrentSellingPlanId() {
        const selectedOption = this.querySelector('input[type="radio"]:checked');
        return selectedOption?.dataset.sellingPlanId || '';
      }

      validateSubscriptionQuantity() {
        return !this.skusToAdd; // Allow add to cart only if no limit is set
      }

      getMetafieldSkusToAdd() {
        try {
          const metafieldValue = this.dataset.subscriptionSkusToAdd;
          if (!metafieldValue) return null;

          const value = parseInt(metafieldValue, 10);
          return !isNaN(value) && value > 0 ? value : null;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error parsing subscription SKUs metafield:', error);
          return null;
        }
      }

      getFrequencyText(sellingPlanName) {
        if (!sellingPlanName) return '';

        // Replace "Deliver every" with "a" for month or with "every" for other periods
        let frequencyText = sellingPlanName.replace(this.translations.deliver_every, '').trim();

        if (frequencyText === this.translations.week) {
          return this.translations.frequency_week;
        }
        if (frequencyText === this.translations.month) {
          return this.translations.frequency_month;
        }
        if (frequencyText === this.translations.year) {
          return this.translations.frequency_year;
        }
        return `${this.translations.frequency_every} ${frequencyText}`;
      }

      getAdjustedPrice(price, rawPrice = false) {
        if (!this.skusToAdd || (!this.isSubscriptionSelected() && !rawPrice)) {
          return price;
        }

        // Handle numeric price
        if (typeof price === 'number') {
          return price * this.skusToAdd;
        }

        // Handle formatted money strings
        const numericPrice = parseFloat(price.replace(/[^0-9.-]+/g, ''));
        if (!isNaN(numericPrice)) {
          return this.formatMoney(numericPrice * 100 * this.skusToAdd);
        }

        return price;
      }

      syncPurchaseTypeSelect(purchaseTypeValue) {
        if (!this.purchaseTypeSelect) return;

        if (this.purchaseTypeSelect.value !== purchaseTypeValue) {
          // Find the option with the matching value
          const targetOption = Array.from(this.purchaseTypeSelect.options).find(
            (option) => option.value === purchaseTypeValue
          );

          if (targetOption) {
            // Update selected attributes
            Array.from(this.purchaseTypeSelect.options).forEach((option) => {
              option.removeAttribute('selected');
            });
            targetOption.setAttribute('selected', 'selected');

            // Update select value and trigger change
            this.purchaseTypeSelect.value = purchaseTypeValue;
            this.purchaseTypeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      connectedCallback() {
        this.initializeDOMReferences();
        this.initializeEventListeners();
        this.resetState();
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);

        this.syncInitialPurchaseTypeSelectState();

        this.updateFrequencyDropdownText();

        this.recalculateStateForNewVariant();
        this.updateGiftWarningVisibility();
      }

      syncInitialPurchaseTypeSelectState() {
        const checkedRadio = this.querySelector('input[name^="purchaseOption_"]:checked');
        if (checkedRadio && this.purchaseTypeSelect) {
          if (checkedRadio.dataset.radioType === 'selling_plan') {
            this.syncPurchaseTypeSelect(this.translations.subscription);
          } else {
            this.syncPurchaseTypeSelect(this.translations.one_time_purchase);
          }
        }
      }

      updateFrequencyDropdownText() {
        // Replace "Deliver every" with "Ship every" in all dropdown options
        this.frequencySelects.forEach((select) => {
          Array.from(select.options).forEach((option) => {
            const optionText = option.textContent.trim();
            if (optionText.startsWith(this.translations.deliver_every)) {
              option.textContent = optionText.replace(this.translations.deliver_every, this.translations.ship_every);
            }
          });
        });
      }

      disconnectedCallback() {
        this.removeEventListeners();
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      initializeDOMReferences() {
        this.purchaseOptions = this.querySelectorAll('input[name^="purchaseOption_"]');
        this.frequencySelects = this.querySelectorAll('.purchase-options__frequency-select');
        this.productInfo = this.closest('product-info');
        this.quantityWrapper = this.productInfo?.querySelector('.product-form__input--quantity');
        this.quantitySelector = this.productInfo?.querySelector('.product-form__quantity');
        this.quantitySelectorInput = this.productInfo?.querySelector('.quantity__input');
        this.priceContainer = this.productInfo?.querySelector('.price__container');
        this.purchaseTypeSelect = this.productInfo?.querySelector(
          `select[name="options[${this.translations.purchase_type}]"]`
        );
        this.giftWarning = this.productInfo?.querySelector('[data-gift-warning]');
      }

      initializeEventListeners() {
        this.purchaseOptions.forEach((radio) => {
          radio.addEventListener('change', this.handlePurchaseOptionChange);
        });

        this.frequencySelects.forEach((select) => {
          select.addEventListener('change', this.handleFrequencyChange);
        });
      }

      removeEventListeners() {
        this.purchaseOptions.forEach((radio) => {
          radio.removeEventListener('change', this.handlePurchaseOptionChange);
        });

        this.frequencySelects.forEach((select) => {
          select.removeEventListener('change', this.handleFrequencyChange);
        });
      }

      resetState() {
        const selectedOption = this.querySelector('input[type="radio"]:checked');
        if (selectedOption) {
          this.handlePurchaseOptionChange({ target: selectedOption });
        } else {
          if (this.quantitySelector) {
            this.quantitySelector.classList.remove('hidden');
          }
          if (this.quantitySelectorInput) {
            this.quantitySelectorInput.value = 1;
          }
        }
      }

      handlePurchaseOptionChange(event) {
        const option = event.target;
        const isSubscription = option.dataset.radioType === 'selling_plan';

        if (isSubscription) {
          this.syncPurchaseTypeSelect(this.translations.subscription);
        } else {
          this.syncPurchaseTypeSelect(this.translations.one_time_purchase);
        }

        // Toggle quantity input visibility and value
        if (this.quantitySelector) {
          this.quantitySelector.classList.toggle('hidden', isSubscription);
        }
        const buttonsWrapper = this.productInfo?.querySelector('.product-form__buttons-wrapper');
        if (buttonsWrapper) {
          buttonsWrapper.classList.toggle('product-form__buttons-wrapper--subscription', isSubscription);
        }

        // Set quantity input value depending on purchase type
        if (this.quantitySelectorInput) {
          if (isSubscription && this.skusToAdd) {
            this.quantitySelectorInput.value = this.skusToAdd;
          } else {
            this.quantitySelectorInput.value = 1;
          }
        }

        const frequencySelect = this.querySelector('.purchase-options__frequency-select');

        // Handle price display, update frequencySelect tabindex
        if (isSubscription) {
          if (frequencySelect) {
            frequencySelect.setAttribute('tabindex', '0');
            const selectedOption = frequencySelect.options[frequencySelect.selectedIndex];
            this.updatePriceDisplay(
              {
                dataset: {
                  variantPrice: selectedOption.dataset.price,
                  variantCompareAtPrice: selectedOption.dataset.compareAtPrice,
                },
              },
              selectedOption.textContent
            );
          } else {
            // Single subscription option - get frequency from the option's parent
            const subscriptionLabel = option
              .closest('.purchase-options__option')
              .querySelector('.purchase-options__radio-label');
            const frequencyText = this.getFrequencyText(subscriptionLabel.dataset.frequency || '');
            this.updatePriceDisplay(option, frequencyText);
          }
        } else {
          if (frequencySelect) {
            frequencySelect.setAttribute('tabindex', '-1');
          }
          this.updatePriceDisplay(option);
        }

        // Dispatch event for product form
        this.dispatchEvent(
          new CustomEvent('selling-plan:change', {
            bubbles: true,
            detail: {
              value: isSubscription ? option.dataset.sellingPlanId : '',
              variantId: this.variantId,
            },
          })
        );

        // fallback for case where there are no Purchase options option setup
        // In this case both purchase type variants are the same
        if (this.oneTimePurchaseOptionVariant?.id === this.subcriptionPurchaseOptionVariant?.id) {
          this.recalculateStateForNewVariant();
        }
      }

      updateGiftWarningVisibility() {
        if (!this.giftWarning) return;

        const isSubscription = this.isSubscriptionSelected();

        if (isSubscription) {
          this.giftWarning.classList.add('visible');
          this.giftWarning.removeAttribute('aria-hidden');
        } else {
          this.giftWarning.classList.remove('visible');
          this.giftWarning.setAttribute('aria-hidden', 'true');
        }
      }

      async checkCartQuantityAndUpdateButton(sellingPlanId) {
        try {
          if (this.productInfo.dataset.productHidden === '1') {
            return;
          }

          const addToCartButton = this.productInfo?.querySelector('button[type="submit"]');
          const addToCartButtonText = addToCartButton.querySelector('span');
          if (!addToCartButton || !addToCartButtonText) return;
          if (!this.skusToAdd) {
            addToCartButtonText.textContent = this.translations.add_subscription_to_cart;
            return;
          }

          addToCartButtonText.textContent = '...';

          const response = await fetch(window.location.origin + '/cart.js');
          const cart = await response.json();

          // Find items in cart with same variant ID and selling plan
          const matchingItems = cart.items.filter(
            (item) =>
              item.variant_id.toString() === this.variantId.toString() &&
              item.selling_plan_allocation?.selling_plan.id.toString() === sellingPlanId.toString()
          );

          // Calculate total quantity of matching items
          const cartQuantity = matchingItems.reduce((sum, item) => sum + item.quantity, 0);

          // Update button state if cart quantity matches or exceeds skusToAdd
          const shouldRestrict = cartQuantity >= this.skusToAdd;
          const maxQuantityMessage = this.productInfo.querySelector('#cart-max-quantity-message');

          if (shouldRestrict) {
            maxQuantityMessage?.removeAttribute('aria-hidden');
            addToCartButton.classList.add('button--is-restricted');
            addToCartButton.setAttribute('aria-disabled', 'true');

            // Connect the button to the explanatory message for screen readers
            addToCartButton.setAttribute('aria-describedby', 'cart-max-quantity-message');

            addToCartButtonText.textContent = this.translations.maximum_quantity_in_cart;
          } else {
            addToCartButton.classList.remove('button--is-restricted');
            addToCartButton.removeAttribute('aria-disabled');
            addToCartButton.removeAttribute('aria-describedby');
            maxQuantityMessage?.setAttribute('aria-hidden', 'true');
            addToCartButtonText.textContent = this.translations.add_subscription_to_cart;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error checking cart quantities:', error);
        }
      }

      handleFrequencyChange(event) {
        const select = event.target;
        const selectedOption = select.options[select.selectedIndex];
        const price = selectedOption.dataset.price;
        const compareAtPrice = selectedOption.dataset.compareAtPrice;
        const sellingPlanId = select.value;
        const frequencyText = selectedOption.textContent;

        const subscriptionRadio = this.querySelector('input[data-radio-type="selling_plan"]');
        if (subscriptionRadio) {
          subscriptionRadio.dataset.sellingPlanId = sellingPlanId;
          subscriptionRadio.dataset.variantPrice = price;
          subscriptionRadio.dataset.variantCompareAtPrice = compareAtPrice;

          if (!subscriptionRadio.checked) {
            subscriptionRadio.checked = true;
            subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        this.updatePriceDisplay(
          {
            dataset: {
              variantPrice: price,
              variantCompareAtPrice: compareAtPrice,
              radioType: 'selling_plan',
            },
          },
          frequencyText
        );

        // Update price next to "Subscribe"
        this.updateSubscribePriceDisplay(subscriptionRadio, price);

        // Dispatch event for product form
        this.dispatchEvent(
          new CustomEvent('selling-plan:change', {
            bubbles: true,
            detail: {
              value: sellingPlanId,
              variantId: this.variantId,
            },
          })
        );

        this.checkCartQuantityAndUpdateButton(sellingPlanId);
      }

      updateSubscribePriceDisplay(subscriptionRadio, price) {
        if (!subscriptionRadio) return;

        const priceDisplay = subscriptionRadio
          .closest('.purchase-options__label')
          .querySelector('.purchase-options__price');

        if (!priceDisplay) return;
        let priceText = `${this.translations.subscribe} • `;
        // Add bags count if metafield is set
        if (this.skusToAdd) {
          priceText += `${this.skusToAdd} ${this.translations.bags} • `;
          // Apply multiplication for display
          const adjustedPrice = this.getAdjustedPrice(price, true);
          priceText += adjustedPrice;
        } else {
          priceText += price;
        }
        priceDisplay.textContent = priceText;
      }

      updatePriceDisplay(option, frequencyText = '') {
        if (!this.priceContainer) return;

        const price = option.dataset.variantPrice;
        const compareAtPrice = option.dataset.variantCompareAtPrice;

        const regularPriceElement = this.priceContainer.querySelector('.price__regular');
        const salePriceElement = this.priceContainer.querySelector('.price__sale');

        // Add frequency text to the price if it's a subscription
        const isSubscription = option.dataset.radioType === 'selling_plan' || this.isSubscriptionSelected();

        const formatPriceWithFrequency = (priceString) => {
          if (!isSubscription && frequencyText) return priceString;

          // If subscription with skusToAdd, use adjusted price
          const adjustedPrice = isSubscription ? this.getAdjustedPrice(priceString) : priceString;

          // Extract the frequency part without "Ship" or "Deliver"
          let frequency = '';
          if (frequencyText.includes(this.translations.ship_every)) {
            frequency = this.getFrequencyText(
              frequencyText.replace(this.translations.ship_every, this.translations.deliver_every)
            );
          } else {
            frequency = frequencyText;
          }
          return `${adjustedPrice} ${frequency}`;
        };

        if (compareAtPrice && compareAtPrice !== price) {
          // Show sale price
          const comparePriceDisplay = regularPriceElement.querySelector('.price-item--regular');

          if (comparePriceDisplay) comparePriceDisplay.innerHTML = formatPriceWithFrequency(price);
        } else {
          // Show regular price
          const regularPrice = regularPriceElement.querySelector('.price-item--regular');
          if (regularPrice) regularPrice.innerHTML = formatPriceWithFrequency(price);
        }
      }

      get oneTimePurchaseOptionVariant() {
        const variant = this.querySelector('[data-one-time-purchase-option-variant]')?.innerHTML;
        return variant ? JSON.parse(variant) : null;
      }

      get subcriptionPurchaseOptionVariant() {
        const variant = this.querySelector('[data-subscription-purchase-option-variant]')?.innerHTML;
        return variant ? JSON.parse(variant) : null;
      }

      syncElementsByIds(elements, sourceContainer, destContainer) {
        const syncedProperties = ['checked', 'value', 'selected'];

        if (!Array.isArray(elements)) {
          throw new Error('First argument must be an array of IDs');
        }

        if (!(sourceContainer instanceof Element) || !(destContainer instanceof Element)) {
          throw new Error('Source and destination must be DOM elements');
        }

        for (const element of elements) {
          const source =
            sourceContainer.id === element.id ? sourceContainer : sourceContainer.querySelector(`#${element.id}`);
          const dest = destContainer.id === element.id ? destContainer : destContainer.querySelector(`#${element.id}`);

          if (!source || !dest) return;

          for (const attr of source.attributes) {
            dest.setAttribute(attr.name, attr.value);
            if (syncedProperties.includes(attr.name)) {
              dest[attr.name] = source[attr.name];
            }
          }

          for (const attr of Array.from(dest.attributes)) {
            if (!source.hasAttribute(attr.name)) {
              dest.removeAttribute(attr.name);
            }
          }

          if (element.syncChildren) {
            dest.innerHTML = source.innerHTML;
          }
        }
      }

      handleVariantChange({ data }) {
        const { variant, html, sectionId } = data;
        if (!variant) return;

        const newSubscriptionModule = html.querySelector('subscription-module');

        const syncedElementsByIds = [
          { id: `ProductSubscriptionModule-${sectionId}` },
          { id: `data-one-time-purchase-option-variant-${sectionId}-${this.dataset.productId}`, syncChildren: true },
          {
            id: `data-subscription-purchase-option-variant-${sectionId}-${this.dataset.productId}`,
            syncChildren: true,
          },
          { id: `purchaseOption-${sectionId}-one-time-input` },
          { id: `purchaseOption-${sectionId}-subscription-input` },
          { id: `purchaseOption-${sectionId}-subscription-label`, syncChildren: true },
          { id: `purchaseOption-${sectionId}-frequency` },
          { id: `subscription-badge-${this.dataset.productId}`, syncChildren: true },
          { id: `selling-plan-description-${this.dataset.productId}` },
          ...Array.from(html.querySelectorAll('.purchase-options__frequency-select')).map((el) => ({
            id: el.id,
            syncChildren: true,
          })),
        ];

        this.syncElementsByIds(syncedElementsByIds, newSubscriptionModule, this);

        // Initialize core state properties
        this.variantId = this.dataset.variantId;
        this.isSubscriptionOnly = Boolean(this.dataset.subscriptionOnly);
        this.skusToAdd = this.getMetafieldSkusToAdd();

        this.disconnectedCallback();
        this.connectedCallback();
      }

      recalculateStateForNewVariant() {
        const isSubscription = this.isSubscriptionSelected();
        if (isSubscription && this.skusToAdd) {
          const currentSellingPlanId = this.getCurrentSellingPlanId();
          this.checkCartQuantityAndUpdateButton(currentSellingPlanId);
        } else {
          // Re-enable button for one-time purchase
          // or for subscription but without skusToAdd
          const addToCartButton = this.productInfo?.querySelector('button[type="submit"]');
          if (addToCartButton) {
            // sold out case
            if (!isSubscription && this.dataset.addToCart === 'true') {
              return;
            }

            // for some cases we don't want to reenable the button
            if (
              this.dataset.addToCartDisabledReason === 'sold_out' ||
              this.dataset.addToCartDisabledReason === 'no_purchase_option_available'
            ) {
              return;
            }

            addToCartButton.disabled = false;
            addToCartButton.classList.remove('button--is-restricted');
            addToCartButton.removeAttribute('aria-disabled');
            addToCartButton.removeAttribute('aria-describedby');
            this.productInfo?.querySelector('#cart-max-quantity-message')?.setAttribute('aria-hidden', 'true');
            const addToCartText = isSubscription
              ? this.translations.add_subscription_to_cart
              : this.translations.add_to_cart;
            const addToCartButtonText = addToCartButton.querySelector('span');
            addToCartButtonText.textContent = addToCartText;
          }
        }

        // 1. Update one-time purchase option
        const oneTimePurchaseRadio = this.querySelector('input[data-radio-type="one_time_purchase"]');
        if (oneTimePurchaseRadio && this.oneTimePurchaseOptionVariant) {
          const oneTimePurchaseVariant = this.oneTimePurchaseOptionVariant;
          const variantPrice = this.formatMoney(oneTimePurchaseVariant.price);
          oneTimePurchaseRadio.dataset.variantPrice = variantPrice;
          oneTimePurchaseRadio.dataset.variantId = oneTimePurchaseVariant.id;

          const priceDisplay = oneTimePurchaseRadio
            .closest('.purchase-options__label')
            ?.querySelector('.purchase-options__price');
          if (priceDisplay) {
            priceDisplay.textContent = `• ${variantPrice}`;
          }

          if (!this.isSubscriptionSelected()) {
            this.updatePriceDisplay({
              dataset: {
                variantPrice: variantPrice,
                variantCompareAtPrice: oneTimePurchaseVariant.compare_at_price
                  ? this.formatMoney(oneTimePurchaseVariant.compare_at_price)
                  : '',
                radioType: 'one_time_purchase',
              },
            });
          }
        }

        // 2. Update subscription options
        if (this.subcriptionPurchaseOptionVariant?.selling_plan_allocations?.length) {
          const subcriptionVariant = this.subcriptionPurchaseOptionVariant;
          const subscriptionRadio = this.querySelector('input[data-radio-type="selling_plan"]');
          const frequencySelect = this.querySelector('.purchase-options__frequency-select');

          if (subscriptionRadio && frequencySelect) {
            const currentPlanId = frequencySelect.value;
            const currentAllocation = subcriptionVariant.selling_plan_allocations.find(
              (allocation) => allocation.selling_plan_id.toString() === currentPlanId
            );

            // Update all options
            subcriptionVariant.selling_plan_allocations.forEach((allocation) => {
              const option = frequencySelect.querySelector(`option[value="${allocation.selling_plan_id}"]`);
              if (option) {
                option.dataset.price = this.formatMoney(allocation.price);
                option.dataset.compareAtPrice = this.formatMoney(subcriptionVariant.price);
              }
            });

            // Update current selection if found
            if (currentAllocation) {
              const allocationPrice = this.formatMoney(currentAllocation.price);
              const compareAtPrice = this.formatMoney(subcriptionVariant.price);

              subscriptionRadio.dataset.sellingPlanId = currentAllocation.selling_plan_id;
              subscriptionRadio.dataset.variantPrice = allocationPrice;
              subscriptionRadio.dataset.variantCompareAtPrice = compareAtPrice;

              // Update subscription price display with bags count if applicable
              this.updateSubscribePriceDisplay(subscriptionRadio, allocationPrice);

              if (this.isSubscriptionSelected()) {
                const selectedOption = frequencySelect.options[frequencySelect.selectedIndex];
                this.updatePriceDisplay(
                  {
                    dataset: {
                      variantPrice: allocationPrice,
                      variantCompareAtPrice: compareAtPrice,
                      radioType: 'selling_plan',
                    },
                  },
                  selectedOption.textContent
                );
              }
            }
          }
        }
      }

      formatMoney(cents) {
        return window.Shopify?.formatMoney?.(cents, this.dataset.moneyFormat) || `$${(cents / 100).toFixed(2)}`;
      }
    }
  );
}
